import { Drawer, Grid, MenuItem, SelectChangeEvent, TableRow, useMediaQuery } from "@mui/material";
import { Group } from "Models/group";
import { MemberInGroup } from "Models/memberInGroup";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  GroupForm,
  MaiFooterSubmit,
  Main,
  MainContent,
  MainFooter,
  MainTitle,
  MainTitleLabel,
  StyledTableCell,
  TableLabel,
} from "./style";
import { Close, InfoOutlined } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { GroupType, Status } from "Util/enums";
import SkeletonComponent from "Components/Generic/Skeleton";
import Input from "Components/Form/Input";
import * as yup from "yup";
import Select from "Components/Form/Select";
import { useAppState } from "Context/StateContext";
import useStyles from "./style";
import { useQuery } from "@tanstack/react-query";
import GroupService from "Services/group.service";
import useActions from "../../useAction";
import { v4 as uuid } from "uuid";
import ShipeeziNewGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { Badge } from "reactstrap";
import { AxiosError } from "axios";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import RoleService from "Services/role.service";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import { Roles } from "Models/user";
import { IconTypo } from "../../../../../../../../modules/moving/components/Diary/views/DiaryResourcesView/subcomponent/Viewers/ResourceTaskViewer/TaskCard/styles";
import CircularProgress from "@mui/material/CircularProgress";

export interface GroupDrawerHandles {
  open: (group: Group) => void;
}

export const GroupDrawer = React.forwardRef<GroupDrawerHandles, any>((_props, ref) => {
  const {
    updateGroup,
    createGroup,
    statusConverter,
    errorUniqueGroupName,
    setErrorUniqueGroupName,
    isCreating,
    isUpdating,
    isMutatingGroup,
  } = useActions();
  const [groupForm, setGroupForm] = useState<{
    name: string;
    description: string;
    roleId: number;
    status: string;
  }>({
    name: "",
    description: "",
    roleId: 0,
    status: Status.ACTIVE,
  });
  const classes = useStyles();
  const { currentUser } = useAppState();
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [isOpenGroupDrawer, setIsOpenGroupDrawer] = useState<boolean>(false);
  const [group, setGroup] = useState<Group>();
  const [accessRules, setAccessRules] = useState<Roles[]>();
  const [isOpenGroupInfoViewOnly, setIsOpenGroupInfoViewOnly] = useState<boolean>(false);
  const [isDisabledSubmit, setIsDisabledSubmit] = useState<boolean>(true);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [isDisabledForm, setIsDisabledForm] = useState<boolean>(false);
  const [isShowLoading, setIsShowLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [members, setMembers] = useState<MemberInGroup[]>([]);
  const { t: translate } = useTranslation();
  const arrowDownIcon = require("Assets/icons/polygon.svg");

  const closeGroupInfoViewOnly = () => {
    setIsOpenGroupInfoViewOnly(false);
  };

  const setCurrentPage = (newPage: number) => {
    setPage(newPage > 0 ? newPage - 1 : newPage);
  };

  const { isFetching: isFetchingAccessRules } = useQuery(
    ["roles.list"],
    () =>
      RoleService.getAllIncludeGroups({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    {
      enabled: isOpenGroupDrawer && !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setAccessRules(response.data);
        }
      },
    }
  );

  const { isFetching: isFetchingUsersInGroup } = useQuery(
    ["users-by-group"],
    () => {
      return GroupService.getUsersInGroup({
        groupId: group?.id,
        sortBy: JSON.stringify([["updatedAt", "desc"]]),
      });
    },
    {
      enabled: !!currentUser?.id && !!group?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setMembers(response.data);
      },
      onError: (error: AxiosError<Error>) => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    status: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    roleId: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required(translate("selectAnAccessRule"))
      .moreThan(0, translate("selectAnAccessRule"))
      .typeError(translate("selectAnAccessRule")),
    description: yup.string(),
  });

  const isValidForm = (form: any, fieldName?: string) => {
    try {
      schema.validateSync(
        {
          name: form.name,
          status: form.status,
          roleId: form.roleId,
        },
        { abortEarly: false }
      );
      setErrors([]);
      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        if (fieldName) {
          const errorForField = error?.inner?.find((err) => err?.path === fieldName);
          setErrors([errorForField as any]);
        } else {
          setErrors(error?.inner);
        }
      }

      return false;
    }
  };

  const handleFormChange = (newForm: any, fieldName: string): void => {
    setGroupForm(newForm);
    setIsDisabledSubmit(!isValidForm(newForm, fieldName));
  };

  const handleCleanFields = () => {
    setGroupForm({
      name: "",
      description: "",
      roleId: 0,
      status: Status.ACTIVE,
    });
    setGroup({} as Group);
    setIsDisabledForm(false);
    setErrors([]);
    setMembers([]);
    setIsDisabledSubmit(true);
    setHasFormBeenSubmitted(false);
    setErrorUniqueGroupName(false);
  };

  const groupStatus: { value: string; name: string }[] = useMemo(
    () => [
      { value: Status.ACTIVE, name: Status.ACTIVE },
      { value: Status.INACTIVE, name: Status.INACTIVE },
    ],
    []
  );

  const open = useCallback((group: Group) => {
    setGroup(group);
    setIsOpenGroupDrawer(true);
  }, []);

  const close = useCallback(() => {
    setIsOpenGroupDrawer(false);
    handleCleanFields();
  }, []);

  const fieldHasError = (field: string) => !!errors?.find((error) => error?.path === field);
  const getErrorByField = (field: string) =>
    errors?.find((error) => error?.path === field)?.message;

  const handleSubmit = () => {
    let payload: any = {};
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);
    if (!isValidForm(groupForm)) {
      return;
    }

    setErrorUniqueGroupName(false);
    payload = {
      ...groupForm,
      companyId: currentUser?.companyId,
      type: GroupType.CUSTOM,
      status: groupForm?.status === Status.ACTIVE ? 1 : 0,
      valid: true,
      code: uuid(),
    };

    if (group?.id) {
      updateGroup({
        currentGroup: { ...payload, id: group?.id },
        prevGroup: group,
      });
    } else {
      createGroup(payload);
    }
  };

  const badgeStatus = (status: number) => (
    <Badge
      className={`
          ${classes.statusBadge}
          ${
            statusConverter(status) === Status.ACTIVE
              ? classes.statusBadgeActive
              : classes.statusBadgeInactive
          }
      `}
    >
      {statusConverter(status)}
    </Badge>
  );

  const memberColumns: Column[] = [
    {
      name: "memberId",
      title: translate("code"),
      formatComponent: (memberId) => <span className={classes.rowStyle}>{memberId ?? "_"}</span>,
    },
    {
      name: "name",
      title: translate("memberName"),
      formatComponent: (name) => <span className={classes.rowStyle}>{name ?? "_"}</span>,
    },
    {
      name: "memberStatus",
      title: translate("status"),
      formatComponent: (status) => (status ? badgeStatus(status) : "-"),
      hideSearch: true,
    },
  ];

  useEffect(() => {
    if (errorUniqueGroupName) {
      setErrors([
        new yup.ValidationError(
          translate("theGroupNameAlreadyExistsPleaseChooseADifferentName", {
            groupName: groupForm.name,
          }),
          groupForm.name,
          "name"
        ),
      ]);
      setIsDisabledSubmit(true);
    }

    if (
      hasFormBeenSubmitted &&
      !isCreating &&
      !isUpdating &&
      !errorUniqueGroupName &&
      isOpenGroupDrawer &&
      !isMutatingGroup
    ) {
      close();
    }
  }, [
    errorUniqueGroupName,
    hasFormBeenSubmitted,
    isOpenGroupDrawer,
    isCreating,
    isUpdating,
    isMutatingGroup,
  ]);

  useEffect(() => {
    setIsDisabledForm(isMutatingGroup);
    setIsShowLoading(isMutatingGroup);
  }, [isMutatingGroup]);

  useEffect(() => {
    if (group?.id) {
      setGroupForm({
        name: group?.name ?? "",
        description: group?.description ?? "",
        roleId: group?.roleId,
        status: group?.status === 1 ? Status.ACTIVE : Status.INACTIVE,
      });

      setIsDisabledForm(group?.type === GroupType.SYSTEM);
      setIsDisabledSubmit(false);
    }
    setIsOpenGroupInfoViewOnly(group?.type === GroupType.SYSTEM);
  }, [group]);

  const renderMembersInGroup = () => {
    if (!members || members?.length === 0) {
      return (
        <TableContainer className={classes.styledTableContrainer} component={Paper}>
          <Table>
            <TableHead className={classes.styledTableHead}>
              <TableRow>
                <StyledTableCell className={classes.styledTableCell}>
                  <div className={classes.styledTableCellContent}>
                    {translate("code")}
                    <IconTypo>
                      <img
                        src={arrowDownIcon}
                        alt=""
                        width={10}
                        height={10}
                        style={{
                          color: "#475467",
                          cursor: "pointer",
                        }}
                      />
                    </IconTypo>
                  </div>
                </StyledTableCell>
                <StyledTableCell className={classes.styledTableCell}>
                  <div className={classes.styledTableCellContent}>
                    {translate("memberName")}
                    <IconTypo>
                      <img
                        src={arrowDownIcon}
                        alt=""
                        width={10}
                        height={10}
                        style={{
                          color: "#475467",
                          cursor: "pointer",
                        }}
                      />
                    </IconTypo>
                  </div>
                </StyledTableCell>
                <StyledTableCell className={classes.styledTableCell}>
                  <div className={classes.styledTableCellContent}>
                    {translate("status")}
                    <IconTypo>
                      <img
                        src={arrowDownIcon}
                        alt=""
                        width={10}
                        height={10}
                        style={{
                          color: "#475467",
                          cursor: "pointer",
                        }}
                      />
                    </IconTypo>
                  </div>
                </StyledTableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
      );
    }

    return (
      <div className={classes.gridWrapper}>
        <ShipeeziNewGrid
          data={members}
          columns={memberColumns}
          keyExtractor={(data) => data.id ?? ""}
          pageSize={20}
          currentPage={page}
          onNextPage={setCurrentPage}
          onPreviousPage={setCurrentPage}
          onGoToPage={setCurrentPage}
          showSelection={false}
        />
      </div>
    );
  };

  const renderGroupInfoForViewOnly = () => {
    if (!isOpenGroupInfoViewOnly) return;

    return (
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <div style={{ marginBottom: "24px" }} className={classes.groupInfo}>
          <InfoOutlined className={classes.groupInfoIcon} />
          <span className={classes.groupInfoContent}>
            {translate(
              "thisIsADefaultSystemGroupThatCannotBeModifiedButYouCanAddOrRemoveMembersFromIt"
            )}
          </span>
          <Close
            style={{ cursor: "pointer" }}
            onClick={closeGroupInfoViewOnly}
            className={classes.groupInfoIcon}
          />
        </div>
      </Grid>
    );
  };

  const renderGroupForm = () => {
    return (
      <GroupForm>
        <Grid container direction="row" alignItems="flex-start">
          {renderGroupInfoForViewOnly()}
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Input
              required
              disabled={isDisabledForm}
              labelClassName={
                fieldHasError("name")
                  ? classes.inputLabel
                  : isFetchingAccessRules || isDisabledForm
                  ? classes.labelDisabled
                  : classes.label
              }
              name="name"
              label={translate("groupName")}
              error={fieldHasError("name")}
              errorMessage={getErrorByField("name")}
              placeholder={translate("enterNameForThisGroup")}
              value={groupForm?.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFormChange({ ...groupForm, name: e?.target?.value }, "name")
              }
            />
          </Grid>
          <Grid style={{ marginTop: "12px" }} item xs={12} sm={12} md={12} lg={12}>
            <Input
              disabled={isDisabledForm}
              name="description"
              label={translate("description")}
              labelClassName={
                isFetchingAccessRules || isDisabledForm ? classes.labelDisabled : classes.label
              }
              error={fieldHasError("description")}
              errorMessage={getErrorByField("description")}
              placeholder={translate("describeThisGroupInFewWords")}
              value={groupForm?.description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFormChange({ ...groupForm, description: e?.target?.value }, "description")
              }
            />
          </Grid>
          <Grid style={{ marginTop: "12px" }} item xs={12} sm={12} md={6} lg={6}>
            <div style={{ marginRight: useMediaQuery("(max-width: 768px)") ? "0px" : "6px" }}>
              <SkeletonComponent isLoading={isFetchingAccessRules}>
                <Select
                  required
                  labelStyle={{
                    fontFamily: "Manrope",
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    verticalAlign: "middle",
                    marginBottom: "4px",
                    color: fieldHasError("roleId")
                      ? "#D92D20"
                      : isFetchingAccessRules || isDisabledForm
                      ? "#98A2B3"
                      : "#004D95",
                  }}
                  tabIndex={0}
                  disabled={isFetchingAccessRules || isDisabledForm}
                  name="roleId"
                  label={translate("accessRule")}
                  error={fieldHasError("roleId")}
                  errorMessage={getErrorByField("roleId")}
                  placeholder={translate("selectAnAccessRule")}
                  value={groupForm?.roleId}
                  onChange={(e: SelectChangeEvent<any>) =>
                    handleFormChange({ ...groupForm, roleId: Number(e?.target?.value) }, "roleId")
                  }
                >
                  {accessRules?.map((accessRule: any) => (
                    <MenuItem
                      className={classes.selectItem}
                      key={accessRule.id}
                      value={accessRule.id}
                      data-cy={accessRule.name}
                    >
                      {accessRule.name}
                    </MenuItem>
                  ))}
                </Select>
              </SkeletonComponent>
            </div>
          </Grid>
          <Grid style={{ marginTop: "12px" }} item xs={12} sm={12} md={6} lg={6}>
            <div style={{ marginLeft: useMediaQuery("(max-width: 768px)") ? "0px" : "6px" }}>
              <Select
                disabled={isDisabledForm}
                required
                labelStyle={{
                  fontFamily: "Manrope",
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "0.1px",
                  verticalAlign: "middle",
                  marginBottom: "4px",
                  color: fieldHasError("status")
                    ? "#D92D20"
                    : isFetchingAccessRules || isDisabledForm
                    ? "#98A2B3"
                    : "#004D95",
                }}
                tabIndex={0}
                name="status"
                label={translate("status")}
                error={fieldHasError("status")}
                errorMessage={getErrorByField("status")}
                value={groupForm?.status}
                onChange={(e: SelectChangeEvent<any>) =>
                  handleFormChange({ ...groupForm, status: e?.target?.value }, "status")
                }
              >
                {groupStatus?.map((status) => (
                  <MenuItem className={classes.selectItem} key={status.value} value={status.value}>
                    {status.name}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </Grid>
          <Grid style={{ marginTop: "24px" }} item xs={12} sm={12} md={12} lg={12}>
            <TableLabel style={{ marginBottom: "4px" }}>
              {translate("membersInGroup")} ({members?.length ?? 0})
            </TableLabel>
            <SkeletonComponent isLoading={isFetchingUsersInGroup}>
              <div className={classes.gridWrapper}>{renderMembersInGroup()}</div>
            </SkeletonComponent>
          </Grid>
        </Grid>
      </GroupForm>
    );
  };

  const renderGroupFooter = () => {
    if (group?.type === GroupType.SYSTEM) {
      return (
        <MainFooter>
          <Button className={classes.btnClose} variant="text" onClick={close}>
            {translate("close")}
          </Button>
        </MainFooter>
      );
    }

    return (
      <MainFooter>
        <Button
          variant="text"
          className={classes.btnClose}
          onClick={close}
          disabled={isMutatingGroup}
        >
          {translate("close")}
        </Button>
        <MaiFooterSubmit>
          <Button
            className={classes.btnSubmit}
            disabled={isDisabledForm || isDisabledSubmit}
            variant="contained"
            style={{
              backgroundColor: isDisabledForm || isDisabledSubmit ? "" : "#0055A4",
            }}
            onClick={handleSubmit}
          >
            {group?.id ? translate("update") : translate("addGroup")}
          </Button>
        </MaiFooterSubmit>
      </MainFooter>
    );
  };

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  return (
    <Drawer
      anchor="right"
      ModalProps={{
        keepMounted: false,
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disablePortal: true,
      }}
      PaperProps={{
        style: {
          overflow: "hidden",
          backgroundColor: "transparent",
          color: "inherit",
          width: useMediaQuery("(max-width: 600px)") ? "100%" : "624px",
        },
      }}
      open={isOpenGroupDrawer}
      onClose={close}
    >
      <Container fullHeight={true}>
        {isShowLoading && (
          <div className={classes.overlayLoading}>
            <CircularProgress color="inherit" />
          </div>
        )}
        <Main>
          <MainTitle>
            <MainTitleLabel>{translate("group")}</MainTitleLabel>
            <Close className={classes.closeGroupDrawer} onClick={() => close()} />
          </MainTitle>

          <MainContent>{renderGroupForm()}</MainContent>

          {renderGroupFooter()}
        </Main>
      </Container>
    </Drawer>
  );
});
